import { AfterViewInit, Directive, ElementRef } from '@angular/core';
import { createFinePointerMediaQuery } from 'app/common/viewport-breakpoints';

/**
 * Focuses the first native input rendered inside the host element.
 *
 * Meant for the first field of a form, and placed on the input component itself
 * (`<app-input-line-form appAutofocus>`) rather than on the native input, which those components
 * own. `app-input-line-form`, `app-input-password` and `ui-text-box` each render exactly one
 * <input>, so the lookup is unambiguous. That contract is pinned by
 * autofocus.directive.integration.spec.ts.
 *
 * Prefer this over the `@ViewChild('firstInput')` + `renderer.selectRootElement(...).focus()` +
 * `setTimeout` idiom still copied across several dialogs; this replaces it.
 *
 * On the sign-up wizard the steps are `*ngSwitchCase` blocks, so each step's fields are created as
 * that step becomes active and this runs again for the new step with no step tracking needed.
 *
 * Deliberately not used on sign-in's email: Chrome opens its saved-credentials list whenever that
 * field takes focus, and nothing short of making the field unfillable prevents it. See the notes
 * on that field in sign-in.component.html.
 */
@Directive({
    selector: '[appAutofocus]'
})
export class AutofocusDirective implements AfterViewInit {

    constructor(private elementRef: ElementRef<HTMLElement>) {
    }

    ngAfterViewInit(): void {
        // No deferral needed: Angular creates a child component's view before running the
        // AfterViewInit hooks of the view that declares it, so the input is already rendered.
        if (this.hasFinePointer()) {
            this.elementRef.nativeElement.querySelector('input')?.focus();
        }
    }

    /**
     * Only autofocus where there is a real pointer. On a phone the soft keyboard would open
     * unprompted, cover the card and shift the layout, and in the sign-up wizard it would reopen
     * on every step.
     */
    private hasFinePointer(): boolean {
        return createFinePointerMediaQuery()?.matches === true;
    }
}
